import {
  ChevronDown,
  ChevronUp,
  DatabaseMessaging,
  TableSplit,
} from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Icon,
  Input,
  LoadingIcon,
  Tab,
  Tabs,
  Textarea,
  Tooltip,
} from 'vet-bones/design-system/components';

import { DatabaseRefresh } from 'src/components/icons/DatabaseRefresh';
import { DataSourceTable } from 'src/constants/dataSourceTable';
import { TEST_IDS } from 'src/constants/testIds';
import { VirtualGraphTableTree } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphTableTree';
import * as copy from 'src/templates/copy';

import styles from 'src/components/resourceDialog/styles/resourceDisplayPanel.module.scss';

type VirtualGraphTableSelectionProps = {
  tables: DataSourceTable[];
  // Single-select mode props
  selectedTable?: string | null;
  onSelectTable?: (tableName: string) => void;
  // SQL query props (single query mode)
  customQuery?: string;
  onCustomQueryChange?: (query: string) => void;
  onRunQuery?: (query?: string) => void;
  isUsingCustomQuery: boolean;
  onModeChange: (isCustomQuery: boolean) => void;
  isQueryRunning?: boolean;
  /**
   * Disables custom SQL on this source (e.g. when resource previews are off):
   * hides the mode tabs and the Run button so a new custom query can't be
   * selected or run. An existing custom-SQL resource is still shown so its
   * query text can be edited.
   */
  customQueryDisabled?: boolean;
  isTableTreeOpen?: boolean;
  onTableTreeOpenChange?: (isOpen: boolean) => void;
  expandAllTables?: boolean;
  // Multi-select mode props
  multiSelect?: boolean;
  checkedTables?: string[];
  onCheckTable?: (tableName: string) => void;
  onCheckAllInSchema?: (
    catalog: string,
    schema: string,
    tableNames: string[]
  ) => void;
  onCheckAllInCatalog?: (catalog: string, tableNames: string[]) => void;
  previewTable?: string | null;
  onPreviewTable?: (tableName: string) => void;
  // Multi-select SQL query props
  onAddCustomQuery?: (query: string) => void;
  // Refresh metadata
  onRefreshMetadata?: () => void;
  isRefreshingMetadata?: boolean;
  isMetadataRefreshable?: boolean;
};

export const VirtualGraphTableSelection: React.VFC<
  VirtualGraphTableSelectionProps
> = ({
  tables,
  selectedTable,
  onSelectTable,
  customQuery = '',
  onCustomQueryChange,
  onRunQuery,
  isUsingCustomQuery,
  onModeChange,
  isQueryRunning,
  customQueryDisabled = false,
  isTableTreeOpen: isTableTreeOpenProp,
  onTableTreeOpenChange,
  expandAllTables,
  // Multi-select props
  multiSelect = false,
  checkedTables = [],
  onCheckTable,
  onCheckAllInSchema,
  onCheckAllInCatalog,
  previewTable,
  onPreviewTable,
  onAddCustomQuery,
  onRefreshMetadata,
  isRefreshingMetadata,
  isMetadataRefreshable,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [openCatalogs, setOpenCatalogs] = React.useState<Set<string>>(
    new Set()
  );
  const [openSchemas, setOpenSchemas] = React.useState<Set<string>>(new Set());
  // Local state for SQL query in multi-select mode - initialized from prop
  const [localCustomQuery, setLocalCustomQuery] = React.useState(customQuery);

  const isTableSelectionOpen = isTableTreeOpenProp ?? true;
  const setIsTableSelectionOpen = (isOpen: boolean) => {
    onTableTreeOpenChange?.(isOpen);
  };

  // Sync localCustomQuery with customQuery prop when parent updates it
  // (e.g., when viewing a saved query)
  React.useEffect(() => {
    if (multiSelect && customQuery && isUsingCustomQuery) {
      setLocalCustomQuery(customQuery);
    }
  }, [multiSelect, customQuery, isUsingCustomQuery]);

  // Get the current query value based on mode
  const currentQuery = multiSelect ? localCustomQuery : customQuery;
  const handleQueryChange = multiSelect
    ? setLocalCustomQuery
    : onCustomQueryChange;

  // Handle adding a custom query in multi-select mode
  const handleAddQuery = () => {
    if (multiSelect && onAddCustomQuery && localCustomQuery.trim()) {
      onAddCustomQuery(localCustomQuery.trim());
      setLocalCustomQuery('');
    }
  };

  return (
    <>
      {customQueryDisabled ? null : (
        <Tabs size="md" variant="square">
          <Tab
            leftIcon={TableSplit}
            onClick={() => onModeChange(false)}
            selected={!isUsingCustomQuery}
            size="md"
            testId={TEST_IDS.projectVirtualGraphPanel.customQuery.tableTab}
          >
            {copy.components.virtualGraphPreviewDialog.tableSelection}
          </Tab>
          <Tab
            leftIcon={DatabaseMessaging}
            onClick={() => onModeChange(true)}
            selected={isUsingCustomQuery}
            size="md"
            testId={TEST_IDS.projectVirtualGraphPanel.customQuery.queryTab}
          >
            {copy.components.virtualGraphPreviewDialog.sqlQueryTab}
          </Tab>
        </Tabs>
      )}

      <div className={styles.tableSelectionContentWrapper}>
        {isUsingCustomQuery ? (
          <div className={styles.tableSelectionWrapper}>
            <div className={styles.tableSelectionHeader}>
              <div className={styles.tableSelectionTitle}>
                <Body size="lg" weight="semibold">
                  {copy.components.virtualGraphPreviewDialog.sqlQuery}
                </Body>
              </div>
              {multiSelect && !onRunQuery ? (
                <Button
                  disabled={!localCustomQuery.trim()}
                  onClick={handleAddQuery}
                  size="sm"
                  variant="primary-accent"
                >
                  {
                    copy.components.projectResourceDialog
                      .projectVirtualGraphPanel.form.addQuery
                  }
                </Button>
              ) : (
                <Button
                  disabled={
                    isQueryRunning ||
                    !currentQuery.trim() ||
                    customQueryDisabled
                  }
                  onClick={() => onRunQuery?.(currentQuery)}
                  size="sm"
                  testId={
                    TEST_IDS.projectVirtualGraphPanel.customQuery
                      .validationButton
                  }
                  variant="primary-accent"
                >
                  {
                    copy.components.projectResourceDialog
                      .projectVirtualGraphPanel.form.runQuery
                  }
                </Button>
              )}
            </div>
            <div className={styles.sqlQueryTextarea}>
              <Textarea
                id="sql-query-input"
                label=""
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleQueryChange?.(e.target.value)
                }
                placeholder={
                  copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .form.pasteQuery
                }
                testId={TEST_IDS.projectVirtualGraphPanel.customQuery.field}
                value={currentQuery}
              />
            </div>
          </div>
        ) : (
          <div className={styles.tableSelectionWrapper}>
            <div
              className={styles.tableSelectionHeader}
              data-testid={
                TEST_IDS.projectVirtualGraphPanel.tableSelectionHeader
              }
              onClick={() => setIsTableSelectionOpen(!isTableSelectionOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsTableSelectionOpen(!isTableSelectionOpen);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.tableSelectionTitle}>
                <Icon
                  IconComponent={isTableSelectionOpen ? ChevronUp : ChevronDown}
                  size="md"
                />
                <Body size="lg" weight="semibold">
                  {copy.components.virtualGraphPreviewDialog.tableSelection}
                </Body>
              </div>
              {isMetadataRefreshable ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  role="button"
                  tabIndex={-1}
                >
                  <Tooltip
                    description={
                      copy.components.virtualGraphPreviewDialog
                        .refreshMetadataTooltip
                    }
                    placement="bottom"
                  >
                    <Button
                      disabled={isRefreshingMetadata}
                      leftIcon={
                        isRefreshingMetadata ? LoadingIcon : DatabaseRefresh
                      }
                      onClick={onRefreshMetadata}
                      size="xs"
                      testId={
                        TEST_IDS.projectVirtualGraphPanel.refreshMetadataButton
                      }
                      variant="ghost-gray"
                    />
                  </Tooltip>
                </div>
              ) : null}
              <div
                className={styles.tableSearchInput}
                onClick={(e) => e.stopPropagation()}
                role="button"
                tabIndex={-1}
              >
                <Input
                  clearable
                  id="table-search"
                  label=""
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    copy.components.virtualGraphPreviewDialog.searchPlaceholder
                  }
                  size="md"
                  value={searchQuery}
                />
              </div>
            </div>
            {isTableSelectionOpen ? (
              <div className={styles.tableSelectionContent}>
                <VirtualGraphTableTree
                  checkedTables={checkedTables}
                  expandAll={expandAllTables}
                  multiSelect={multiSelect}
                  onCheckAllInCatalog={onCheckAllInCatalog}
                  onCheckAllInSchema={onCheckAllInSchema}
                  onCheckTable={onCheckTable}
                  onOpenCatalogsChange={setOpenCatalogs}
                  onOpenSchemasChange={setOpenSchemas}
                  onPreviewTable={onPreviewTable}
                  onSelectTable={onSelectTable}
                  openCatalogs={openCatalogs}
                  openSchemas={openSchemas}
                  previewTable={previewTable}
                  searchQuery={searchQuery}
                  selectedTable={selectedTable}
                  tables={tables}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};
